import React, { useEffect, useMemo, useState } from "react";
import { Layers, Loader2, X } from "lucide-react";

const PHASE_LABELS = {
  load: "Loading plan…",
  create: "Creating template…",
  meals: "Copying meals…",
  options: "Copying meal options…",
  foods: "Copying foods…",
};

/**
 * Name + visibility prompt for turning a user's (possibly customized) plan
 * into a brand-new, reusable template. Presentational + validation only — the
 * actual save pipeline (saveTargetPlanAsTemplate) is called by the caller.
 */
export default function SaveAsTemplateModal({
  isOpen,
  onClose,
  onContinue,
  planTitle = "",
  dietCommonUtils,
  existingTemplateNames = [],
  continuing = false,
  phase = null,
  errorMessage = "",
  partialTemplateId = null,
  onOpenPartialTemplate,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [security, setSecurity] = useState("private");

  useEffect(() => {
    if (!isOpen) return;
    setName(planTitle ? `Copy · ${planTitle}` : "New template");
    setDescription(planTitle ? `Saved from plan: ${planTitle}` : "");
    setSecurity("private");
  }, [isOpen, planTitle]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !continuing) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, continuing]);

  const duplicateName = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;
    return existingTemplateNames.some((n) => n.trim().toLowerCase() === trimmed);
  }, [existingTemplateNames, name]);

  if (!isOpen) return null;

  const getSecurityId = () => {
    const sec = dietCommonUtils?.securities?.find(
      (s) => (s.description || "").toLowerCase() === security.toLowerCase()
    );
    return sec?.id || "";
  };

  const canContinue =
    name.trim().length > 0 && !duplicateName && !continuing && !!dietCommonUtils;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={() => !continuing && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-as-template-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 bg-orange-100 rounded-full shrink-0">
              <Layers className="w-5 h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <h3 id="save-as-template-title" className="text-lg font-bold text-gray-900">
                Save as new template
              </h3>
              {planTitle ? (
                <p className="text-sm text-gray-500 mt-0.5 truncate" title={planTitle}>
                  From plan: {planTitle}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={continuing}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {errorMessage && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm space-y-2">
              <p>{errorMessage}</p>
              {partialTemplateId && (
                <button
                  type="button"
                  onClick={() => onOpenPartialTemplate?.(partialTemplateId)}
                  className="font-medium underline hover:no-underline"
                >
                  Open partial template to review or finish it
                </button>
              )}
            </div>
          )}

          <div>
            <label htmlFor="save-template-name" className="block text-sm font-medium text-gray-700 mb-1">
              New template name
            </label>
            <input
              id="save-template-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={continuing}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none disabled:opacity-70"
            />
            {duplicateName && (
              <p className="text-xs text-amber-700 mt-1">
                A template with this name already exists. Choose a different name.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="save-template-desc" className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="save-template-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={continuing}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none resize-none disabled:opacity-70"
            />
          </div>

          <div>
            <label htmlFor="save-template-security" className="block text-sm font-medium text-gray-700 mb-1">
              Visibility
            </label>
            <select
              id="save-template-security"
              value={security}
              onChange={(e) => setSecurity(e.target.value)}
              disabled={continuing}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none disabled:opacity-70"
            >
              <option value="private">Private</option>
              <option value="public">Public</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {security === "private"
                ? "Only you can see and use this template."
                : "Everyone can see and use this template."}
            </p>
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={continuing}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onContinue({
                name: name.trim(),
                description: description.trim(),
                securityId: getSecurityId(),
              })
            }
            disabled={!canContinue}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-orange-primary hover:bg-orange-600 rounded-lg disabled:opacity-50"
          >
            {continuing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {PHASE_LABELS[phase] || "Saving…"}
              </>
            ) : (
              "Save as template"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
